import * as React from 'react';
import moment from 'moment';
import Tooltip from '@ui-library/tooltip';
import Svg from '@svg/.';
import Loading from '@ui-library/Loading';
import { Flex } from '@ui-library/misc';

type SyncRunType = {
  finishedAt?: Date;
  startedAt?: Date;
  error?: {
    code?: string;
    producedAt?: Date;
    message: string;
  };
  resourceRun: {
    importTask: {
      error: unknown;
    };
    exportTask: {
      error: unknown;
    };
  }[];
  importTask: {
    error: unknown;
  };
  exportTask: {
    error: unknown;
  };
};

function isRunning(syncRun: SyncRunType): boolean {
  return !syncRun.finishedAt || moment(syncRun.startedAt).isAfter(moment(syncRun.finishedAt));
}

function hasError(syncRun: SyncRunType): boolean {
  const isError =
    syncRun &&
    syncRun.resourceRun &&
    syncRun.resourceRun.some(
      (resource) =>
        !!(
          (resource.importTask && resource.importTask.error) ||
          (!!resource.exportTask && resource.exportTask.error)
        ),
    );
  return (
    (!isRunning(syncRun) && isError) ||
    !!(
      (syncRun.importTask && syncRun.importTask.error) ||
      (!!syncRun.exportTask && syncRun.exportTask.error)
    )
  );
}

function hasFailed(syncRun: SyncRunType): boolean {
  return !!syncRun.error;
}

function isSuccessful(syncRun: SyncRunType): boolean {
  return !isRunning(syncRun) && !hasError(syncRun) && !hasFailed(syncRun);
}

export type ISyncStatus =
  | 'syncing'
  | 'error'
  | 'success'
  | 'paused'
  | 'failed'
  | 'initializing'
  | 'creating';
function getSyncStatus(
  syncRun: SyncRunType,
  pipeInfo?: { paused?: boolean; creating?: boolean },
): ISyncStatus | undefined {
  if (pipeInfo && pipeInfo.creating) {
    return 'creating';
  }
  if (pipeInfo && pipeInfo.paused) {
    return 'paused';
  }
  if (!syncRun) {
    return undefined;
  }
  if (hasFailed(syncRun)) {
    return 'failed';
  }
  if (isRunning(syncRun)) {
    return 'syncing';
  }
  if (hasError(syncRun)) {
    return 'error';
  }
  if (isSuccessful(syncRun)) {
    return 'success';
  }
  return undefined;
}

export interface CloudSourceSyncStatusProps {
  syncRun?: SyncRunType;
  status?: ISyncStatus | null;
  size?: number;
  label?: string;
  style?: React.CSSProperties;
  withLabel?: boolean;
  withTooltip?: boolean;
  creating?: boolean;
}

export const CloudSourceSyncStatus = ({
  size = 16,
  status,
  withLabel = true,
  label,
  withTooltip,
  syncRun,
  creating,
  style = {},
}: CloudSourceSyncStatusProps) => {
  const syncStatus = status || (syncRun && getSyncStatus(syncRun, { creating }));
  if (!syncStatus) {
    return null;
  }
  let statusIcon;
  let statusLabel;
  const loader = <Loading size={size} />;
  switch (syncStatus) {
    case 'creating':
      statusIcon = loader;
      statusLabel = 'Your pipeline is being created';
      break;
    case 'syncing':
      statusIcon = loader;
      statusLabel = 'Syncing';
      break;
    case 'error':
      statusIcon = <Svg size={size} fill="brown" name="warning" />;
      statusLabel = 'Finished';
      break;
    case 'failed':
      statusIcon = <Svg size={size} fill="red" name="warning" />;
      statusLabel = 'Failed';
      break;
    case 'paused':
      statusIcon = <Svg size={size} fill="grey" name="pause" />;
      statusLabel = 'Paused';
      break;
    default:
      statusIcon = <Svg size={size} color="green" name="check" />;
      statusLabel = 'Finished';
      break;
  }

  let statusItem = statusIcon;
  if (withLabel) {
    statusItem = (
      <div>
        <span style={{ fontWeight: 500, fontSize: size, whiteSpace: 'nowrap' }}>
          {label || statusLabel}
        </span>
      </div>
    );
  }

  if (withTooltip && syncRun?.error) {
    return <Tooltip title={syncRun.error.message || 'Unknown error'}>{statusItem}</Tooltip>;
  }
  return <Flex style={style}>{statusItem}</Flex>;
};
